import { defineComponent } from "vue";

import { COMPONENT_NAME } from "./attributes";

import { SharedWindow } from "@/components/shared";

import { SharedSelect, SharedInputCurrency } from "@/components/shared";
import { UIInput, UIButton } from "@/components/ui";

import { SelectOptionFactory } from "@/shared/factory/select";
import { SharedSelectOption } from "@/components/shared/select/model";
import {
  AdminCurrencyShowResponse,
  AdminChainListResponse,
} from "@/shared/repository/modules/admin/currency/repo";
import { WalletFilterType } from "@/shared/repository/modules/wallet/enums";
import { WalletListResponse } from "@/shared/repository/modules/wallet/repo";
import { WalletModalFactory } from "@/shared/repository/modules/wallet/factory";
import { AdminApplicationType } from "@/shared/repository/modules/admin/application/enums";

export default defineComponent({
  name: COMPONENT_NAME,
  components: {
    SharedWindow,
    SharedSelect,
    UIInput,
    UIButton,
    SharedInputCurrency,
  },
  data() {
    return {
      walletRepository: this.$projectServices.walletRepository,
      walletRepo: this.$projectServices.walletRepo,
      userRepo: this.$projectServices.userRepo,
      coinId: 0,
      chain: "",
      currencys: <WalletListResponse[]>[],
      chains: <AdminChainListResponse[]>[],
      addressTag: "",
      address: "",
      amount: 0,
      balances: <WalletListResponse[]>[],
      isLoading: false,
    };
  },
  async created(): Promise<void> {
    const [currencys] = await Promise.all([
      this.walletRepository.list(
        WalletFilterType.crypto,
        this.userRepo.userInfo.id
      ),
    ]);
    this.currencys = currencys.filter((currency) => currency.amount > 0);
    if (this.walletRepo.modal.currencyId) {
      this.coinId = this.walletRepo.modal.currencyId;
      await this.loadChains();
    }
  },
  computed: {
    displayedCurrencyOptions(): SharedSelectOption[] {
      return this.currencys
        .filter(
          (currency) => currency.type === "crypto" && currency.frozen === false
        )
        .map((currency) =>
          SelectOptionFactory({
            id: currency.id,
            label: currency.name.toUpperCase(),
            value: currency.id,
            icon: currency.name.toLowerCase(),
            isActive: false,
          })
        );
    },

    displayedChainOptions(): SharedSelectOption[] {
      return this.chains.map((chain) =>
        SelectOptionFactory({
          label:
            chain.chain +
            " (Min: " +
            chain.withdrawMin +
            " Fee: " +
            chain.withdrawFee +
            ")",
          value: chain.chain,
          isActive: false,
        })
      );
    },

    displayedCurrencyIcon(): string {
      return (
        this.currencys.find((currency) => currency.id === this.coinId)?.name ??
        ""
      );
    },

    displayedCurrency(): AdminCurrencyShowResponse | undefined {
      return this.currencys.find((currency) => currency.id === this.coinId);
    },

    isDisabled(): boolean {
      return (
        this.coinId === 0 ||
        this.chain === "" ||
        this.address === "" ||
        !this.amount ||
        this.amount <
          +(
            this.chains.find((chain) => chain.chain === this.chain)
              ?.withdrawMin ?? 0
          ) ||
        this.amount >
          (this.currencys.find((currency) => currency.id === this.coinId)
            ?.amount ?? 0)
      );
    },

    errorAmountMessage(): string {
      const min =
        this.chains.find((chain) => chain.chain === this.chain)?.withdrawMin ??
        0;
      if (this.amount < +min) {
        return "Minimum withdrawal amount is " + min;
      }
      const max =
        this.currencys.find((currency) => currency.id === this.coinId)
          ?.amount ?? 0;
      if (this.amount > +max) {
        return "Maximum withdrawal amount is " + max;
      }
      return "";
    },
    resultAmount(): number {
      const fee =
        this.chains.find((chain) => chain.chain === this.chain)?.withdrawFee ??
        0;
      const precision =
        this.currencys.find((currency) => currency.id === this.coinId)
          ?.precision ?? 0;
      const result = this.amount - +fee;
      if (String(result).includes(".")) {
        const [intPart, decPart = ""] = String(result).split(".");
        if (decPart.length > precision) {
          return +(intPart + "." + decPart.slice(0, precision));
        }
      }
      return result;
    },
  },
  methods: {
    async loadChains(): Promise<void> {
      this.chains = await this.walletRepository.chains(this.coinId);
    },

    async handleWithdraw(): Promise<void> {
      const withdraw = {
        coin:
          this.currencys.find((currency) => currency.id === this.coinId)
            ?.name ?? "",
        chain: this.chain,
        tag: this.addressTag,
        address: this.address,
        amount: this.amount,
      };
      try {
        this.isLoading = true;

        await this.walletRepository.withdrawal(withdraw);

        this.walletRepo.UPDATE_MODAL(
          WalletModalFactory({
            type: AdminApplicationType.verificationWalletWithdrawal,
            currencyName: withdraw.coin,
            amount: withdraw.amount,
            total: this.resultAmount,
            currencyId: this.coinId,
            network: this.chain,
            address: this.address + " " + this.addressTag,
          })
        );
      } catch (error) {
        console.log(error);
      } finally {
        this.isLoading = false;
      }
    },

    handleAmountChange(value: number): void {
      this.amount = value;
    },

    handleMin(): void {
      if (!this.chain) return;
      const precision =
        this.currencys.find((currency) => currency.id === this.coinId)
          ?.precision ?? 0;
      const min =
        this.chains.find((chain) => chain.chain === this.chain)?.withdrawMin ??
        0;
      if (String(min).includes(".")) {
        const [intPart, decPart = ""] = String(min).split(".");
        if (decPart.length > precision) {
          this.amount = +(intPart + "." + decPart.slice(0, precision));
          return;
        }
      }
      this.amount = +min;
    },

    handleMax(): void {
      const precision =
        this.currencys.find((currency) => currency.id === this.coinId)
          ?.precision ?? 0;
      const max =
        this.currencys.find((currency) => currency.id === this.coinId)
          ?.amount ?? 0;
      if (String(max).includes(".")) {
        const [intPart, decPart = ""] = String(max).split(".");
        if (decPart.length > precision) {
          this.amount = +(intPart + "." + decPart.slice(0, precision));
          return;
        }
      }
      this.amount = +max;
    },
  },
});
